import React, { useState } from 'react';
import { Clock, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioUnidadeGeradora } from '../../../types';
import { aprovarUg } from './ugAprovacaoActions';
import RejeitarUgModal from './RejeitarUgModal';

interface UGAprovacaoCardProps {
  ug: RateioUnidadeGeradora;
  onChanged: () => void;
}

const UGAprovacaoCard: React.FC<UGAprovacaoCardProps> = ({ ug, onChanged }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [aprovando, setAprovando] = useState(false);
  const [showRejeitar, setShowRejeitar] = useState(false);

  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';
  const pendente = ug.status_aprovacao === 'pendente';

  const handleAprovar = async () => {
    setAprovando(true);
    try {
      await aprovarUg(ug.id);
      addToast('UG aprovada com sucesso.', 'success');
      onChanged();
    } catch (error: any) {
      console.error('Error approving UG', error);
      addToast(error?.message || 'Erro ao aprovar a UG.', 'error');
    } finally {
      setAprovando(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
      {pendente ? (
        <Clock className="mx-auto text-amber-400 mb-3" size={40} />
      ) : (
        <AlertTriangle className="mx-auto text-red-400 mb-3" size={40} />
      )}

      <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins mb-2">{ug.nome}</h2>

      {pendente ? (
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
          Esta UG está <strong>aguardando aprovação</strong> de um Gestor. Ela não pode ser usada no Simulador até ser
          aprovada.
        </p>
      ) : (
        <div className="max-w-md mx-auto mb-6">
          <p className="text-gray-500 dark:text-gray-400 mb-2">Esta UG foi <strong>rejeitada</strong>.</p>
          {ug.motivo_rejeicao && (
            <p className="text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg p-3">
              {ug.motivo_rejeicao}
            </p>
          )}
        </div>
      )}

      {isGestor && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={handleAprovar}
            disabled={aprovando}
            className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {aprovando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {pendente ? 'Aprovar UG' : 'Aprovar mesmo assim'}
          </button>
          {pendente && (
            <button
              onClick={() => setShowRejeitar(true)}
              className="px-5 py-2.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Rejeitar UG
            </button>
          )}
        </div>
      )}

      {showRejeitar && (
        <RejeitarUgModal
          ugId={ug.id}
          onClose={() => setShowRejeitar(false)}
          onRejeitado={() => {
            setShowRejeitar(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default UGAprovacaoCard;
